/**
 * Estilos da Central de Projetos. Mobile first; mesma linguagem dos Relatórios
 * do Legado: lista com divisórias (não mural de cartões), texto neutro e UM
 * sinal de cor por linha. Botões compactos (34 px), lado a lado.
 */
import styled, { css } from "styled-components";

import { BP_MOBILE, BP_TABLET } from "../../styles/canonical/breakpoints";
import { PageContainer } from "../../styles/shared";

export const tabletAcima = `(min-width: ${BP_MOBILE + 1}px)`;
export const desktop = `(min-width: ${BP_TABLET + 1}px)`;

const tom = (nome, parte) => `var(--${nome || "neutral"}-${parte})`;

export const Pagina = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  padding: 12px 12px 32px;

  @media ${tabletAcima} {
    padding: 16px 20px 40px;
    gap: 18px;
  }
`;

export const Topo = styled.div`
  position: sticky;
  top: var(--app-header-height, 0px);
  z-index: 80;
  margin: -12px -12px 0;
  padding: 10px 12px;
  background: ${({ theme }) => theme.surface};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  @media ${tabletAcima} {
    margin: -16px -20px 0;
    padding: 12px 20px;
  }
`;

export const Titulo = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.2;
    color: ${({ theme }) => theme.textPrimary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p {
    margin: 2px 0 0;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media ${desktop} {
    h1 {
      font-size: 20px;
    }
  }
`;

export const Acoes = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const botaoBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 9px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }

  svg {
    flex: 0 0 auto;
  }
`;

export const Botao = styled.button`
  ${botaoBase}
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  /* No celular, botão com ícone mostra só o ícone. */
  @media (max-width: ${BP_MOBILE}px) {
    &[data-icone-no-celular="true"] span {
      display: none;
    }
    &[data-icone-no-celular="true"] {
      padding: 0 9px;
    }
  }
`;

export const BotaoPrimario = styled(Botao)`
  border-color: ${({ theme }) => theme.accent};
  background: ${({ theme }) => theme.accent};
  color: #fff;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.accent};
    filter: brightness(0.95);
  }
`;

/** Ação secundária repetida em lista (ex.: embaixo de cada mensagem): só texto, sem caixa. */
export const BotaoDiscreto = styled.button`
  ${botaoBase}
  min-height: 28px;
  padding: 0 6px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.textTertiary};
  font-size: 12.5px;
  font-weight: 500;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const Faixa = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 14px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.textSecondary};
  min-width: 0;

  strong {
    font-weight: 600;
    color: ${({ $tom }) => tom($tom, "text")};
  }
`;

export const Ponto = styled.span`
  display: inline-block;
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: 1px;
  background: ${({ $tom, theme }) => ($tom && $tom !== "neutral" ? tom($tom, "solid") : theme.border)};
`;

export const Secao = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const SecaoTitulo = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
  min-width: 0;

  h2 {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.textPrimary};
  }

  span {
    font-size: 12px;
    color: ${({ theme }) => theme.textTertiary};
  }

  > :last-child:not(h2):not(span) {
    margin-left: auto;
  }
`;

export const Lista = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;
  min-width: 0;
`;

export const Item = styled.li`
  border-top: 1px solid ${({ theme }) => theme.border};
  min-width: 0;

  &:first-child {
    border-top: 0;
  }
`;

/**
 * Linha clicável. No celular empilha; no desktop vira colunas alinhadas
 * (`$colunas` = grid-template-columns do desktop).
 */
export const Linha = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2px 12px;
  align-items: center;
  padding: 10px 12px;
  min-width: 0;
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};

  &:hover {
    background: ${({ onClick, theme }) => (onClick ? theme.surfaceAlt : "transparent")};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }

  /* Linha de tabela no celular: o nome numa linha e as colunas juntas na
     seguinte, separadas por "·" (cartão compacto, não cinco linhas). */
  @media (max-width: ${BP_TABLET}px) {
    ${({ $colunas, theme }) =>
      $colunas &&
      css`
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;

        > :first-child {
          flex: 1 1 100%;
        }

        > :not(:first-child) {
          flex: 0 1 auto;
          max-width: 100%;
        }

        > :not(:first-child):not(:last-child)::after {
          content: "·";
          margin: 0 6px;
          color: ${theme.textTertiary};
          font-weight: 400;
        }
      `}
  }

  @media ${desktop} {
    grid-template-columns: ${({ $colunas }) => $colunas || "minmax(0, 1fr)"};
  }
`;

export const Principal = styled.div`
  min-width: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  small {
    font-weight: 400;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const Secundario = styled.div`
  min-width: 0;
  font-size: 12.5px;
  color: ${({ $tom, theme }) => ($tom ? tom($tom, "text") : theme.textSecondary)};
  font-weight: ${({ $tom }) => ($tom ? 600 : 400)};
  white-space: ${({ $quebra }) => ($quebra ? "normal" : "nowrap")};
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Vazio = styled.p`
  margin: 0;
  padding: 16px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const Aviso = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.45;
  border: 1px solid ${({ $tom }) => tom($tom || "info", "border")};
  background: ${({ $tom }) => tom($tom || "info", "bg")};
  color: ${({ $tom }) => tom($tom || "info", "text")};
`;

export const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 230px), 1fr));
  gap: 10px;
  min-width: 0;
`;

export const Coluna = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  padding: 10px 12px;
  min-width: 0;

  h3 {
    margin: 0 0 2px;
    font-size: 13.5px;
    font-weight: 700;
    color: ${({ theme }) => theme.textPrimary};
  }

  details summary {
    cursor: pointer;
    font-size: 12.5px;
    color: ${({ theme }) => theme.textSecondary};
  }

  ul {
    margin: 6px 0 0;
    padding-left: 16px;
    font-size: 12.5px;
    color: ${({ theme }) => theme.textPrimary};
  }

  li {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Chip = styled.button`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ $ativo, theme }) => ($ativo ? theme.surfaceAlt : theme.surface)};
  color: ${({ $ativo, theme }) => ($ativo ? theme.textPrimary : theme.textSecondary)};
  font: inherit;
  font-size: 12.5px;
  font-weight: ${({ $ativo }) => ($ativo ? 700 : 500)};
  min-height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  cursor: pointer;
`;

export const Campo = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};

  input,
  select,
  textarea {
    min-height: 34px;
    padding: 0 10px;
    border-radius: 9px;
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    font-size: 13px;
    font-weight: 400;
    min-width: 0;
  }

  textarea {
    padding: 8px 10px;
    min-height: 72px;
    resize: vertical;
  }
`;

export const Filtros = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 160px), 1fr));
  gap: 8px;
  min-width: 0;
`;
